import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, Search, Package, Recycle } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  available: "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30",
  in_use: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/40",
  maintenance: "bg-[#E11D48]/10 text-[#9F1239] border-[#E11D48]/30",
};

export default function EquipOffice() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAssetAdmin = user?.role === "asset_admin";
  const [standard, setStandard] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Multi-select state (map asset_id -> quantity requested). Presence in map = selected.
  const [picked, setPicked] = useState({});
  // Reuse modal state
  const [reuseOpen, setReuseOpen] = useState(false);
  const [reuseStep, setReuseStep] = useState(1);  // Sprint 19: 1=fecha, 2=mensaje
  const [neededUntil, setNeededUntil] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/equip-office/standard-items").then(({ data }) => {
      setStandard(data);
      setQuantities(Object.fromEntries(data.map((d) => [d, 0])));
    });
  }, []);

  const bump = (name, delta) => {
    setQuantities((q) => ({ ...q, [name]: Math.max(0, (q[name] || 0) + delta) }));
  };

  const onMatch = async () => {
    const items = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([name, quantity]) => ({ name, quantity }));
    if (items.length === 0) {
      toast.error("Agrega al menos una cantidad mayor a 0");
      return;
    }
    setLoading(true);
    setPicked({});
    try {
      const { data } = await api.post("/equip-office/match", { items });
      setResults(data.results);
    } catch (e) {
      toast.error("No fue posible empatar el inventario");
    } finally {
      setLoading(false);
    }
  };

  const togglePick = (asset) => {
    setPicked((p) => {
      const next = { ...p };
      if (next[asset.id]) delete next[asset.id];
      else next[asset.id] = Math.max(1, Math.min(1, asset.quantity || 1));
      return next;
    });
  };

  const setPickedQty = (asset, qty) => {
    const max = Math.max(1, asset.quantity || 1);
    const clamped = Math.max(1, Math.min(max, parseInt(qty, 10) || 1));
    setPicked((p) => ({ ...p, [asset.id]: clamped }));
  };

  const pickedList = useMemo(() => {
    const rows = [];
    if (!results) return rows;
    for (const r of results) {
      for (const m of r.matches) {
        if (picked[m.id]) rows.push({ asset: m, quantity: picked[m.id] });
      }
    }
    return rows;
  }, [picked, results]);

  const openReuse = () => {
    if (pickedList.length === 0) {
      toast.error("Selecciona al menos un item");
      return;
    }
    setNeededUntil("");
    setMessage("");
    setReuseStep(1);
    setReuseOpen(true);
  };

  const goToMessageStep = () => {
    if (!neededUntil) {
      toast.error("Elige hasta cuándo lo vas a requerir.");
      return;
    }
    setReuseStep(2);
  };

  const sendReuse = async () => {
    const body = message.trim();
    if (!body) {
      toast.error("Escribe un mensaje para el equipo");
      return;
    }
    if (!neededUntil) {
      toast.error("Elige hasta cuándo lo vas a requerir.");
      setReuseStep(1);
      return;
    }
    setSending(true);
    try {
      const payload = {
        items: pickedList.map((it) => ({ asset_id: it.asset.id, quantity: it.quantity })),
        message: body,
        needed_until: neededUntil,
      };
      const { data } = await api.post("/equip-office/reuse-request-bulk", payload);
      toast.success(
        data.projects_notified > 1
          ? `Solicitud enviada a ${data.projects_notified} equipos.`
          : "Solicitud enviada al equipo del proyecto."
      );
      setReuseOpen(false);
      setPicked({});
      if (data.request_ids?.length === 1) {
        nav(`/reuse-requests/${data.request_ids[0]}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No fue posible enviar la solicitud");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8 pb-24" data-testid="equip-office-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter">Equipamiento provisional.</h1>
        <p className="mt-2 text-sm text-[#5C6A7D] max-w-2xl">Indica qué necesitas. Te mostramos qué hay disponible ahora y qué se va a liberar pronto en otros proyectos.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: checklist */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Lista estándar</div>
            <div className="text-sm font-semibold text-[#0A2540] mt-0.5">Define la cantidad de cada artículo</div>
          </div>
          <div className="divide-y divide-[#E2E8F0]" data-testid="standard-items-list">
            {standard.map((name) => (
              <div key={name} className="flex items-center justify-between px-6 py-3.5">
                <div className="text-sm font-medium text-[#0A2540]">{name}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => bump(name, -1)} className="w-8 h-8 border border-[#E2E8F0] rounded-sm hover:bg-[#FAFAFA] flex items-center justify-center text-[#0A2540]" data-testid={`decrease-${name.toLowerCase().replace(/ /g, '-')}`}>
                    <Minus size={14} />
                  </button>
                  <input
                    type="number" min={0}
                    value={quantities[name] || 0}
                    onChange={(e) => setQuantities({ ...quantities, [name]: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-14 text-center border border-[#E2E8F0] rounded-sm h-8 text-sm font-mono"
                    data-testid={`quantity-${name.toLowerCase().replace(/ /g, '-')}`}
                  />
                  <button onClick={() => bump(name, 1)} className="w-8 h-8 border border-[#E2E8F0] rounded-sm hover:bg-[#FAFAFA] flex items-center justify-center text-[#0A2540]" data-testid={`increase-${name.toLowerCase().replace(/ /g, '-')}`}>
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="p-6 border-t border-[#E2E8F0]">
            <Button onClick={onMatch} disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11 font-semibold tracking-wide" data-testid="find-inventory-button">
              <Search size={16} className="mr-2" />
              {loading ? "Buscando inventario…" : "Buscar inventario disponible"}
            </Button>
          </div>
        </div>

        {/* Right: results */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm min-h-[400px]">
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Coincidencias de inventario</div>
              <div className="text-sm font-semibold text-[#0A2540] mt-0.5">Disponible ahora o por liberarse</div>
            </div>
            {results && results.some((r) => r.matches.length > 0) && (
              <div className="text-[11px] text-[#5C6A7D]" data-testid="picked-count">
                {pickedList.length} seleccionado{pickedList.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          {!results && (
            <div className="px-6 py-16 text-center text-sm text-[#5C6A7D]" data-testid="empty-results">
              <Package className="mx-auto mb-3 text-[#5C6A7D]" size={28} strokeWidth={1.5} />
              Define cantidades y presiona <em>Buscar inventario disponible</em>.
            </div>
          )}
          {results && results.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-[#5C6A7D]">Sin coincidencias. Sube activos primero.</div>
          )}
          {results && results.length > 0 && (
            <div className="divide-y divide-[#E2E8F0]" data-testid="match-results">
              {results.map((r) => (
                <div key={r.name} className="p-6">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="font-display text-lg font-bold text-[#0A2540]">{r.name}</div>
                      <div className="text-xs text-[#5C6A7D] uppercase tracking-[0.18em] mt-0.5">
                        Necesarios {r.needed} · {r.matches.length} coincidencia{r.matches.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  {r.matches.length === 0 ? (
                    <div className="mt-3 text-sm text-[#5C6A7D] italic">Sin inventario disponible en otros proyectos.</div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {r.matches.map((m) => {
                        const isPicked = !!picked[m.id];
                        const rowMax = Math.max(1, m.quantity || 1);
                        return (
                          <div
                            key={m.id}
                            className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${isPicked ? "bg-[#ECFDF5] border-[#10B981]/40" : "bg-[#FAFAFA] border-[#E2E8F0] hover:border-[#0A2540]/30"}`}
                            data-testid={`match-row-${m.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isPicked}
                              onChange={() => togglePick(m)}
                              disabled={isAssetAdmin || m.has_active_reuse_thread}
                              title={
                                isAssetAdmin
                                  ? "El administrador de activos no puede solicitar reutilización"
                                  : m.has_active_reuse_thread
                                    ? "Hay una conversación activa sobre este activo. Espera a que se complete o cancele."
                                    : undefined
                              }
                              className="w-4 h-4 accent-[#10B981] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`match-checkbox-${m.id}`}
                              aria-label={`Seleccionar ${m.name}`}
                            />
                            {m.photos?.[0] ? (
                              <img src={fileUrl(m.photos[0])} alt="" className="w-12 h-12 object-cover rounded-sm" />
                            ) : (
                              <div className="w-12 h-12 bg-white rounded-sm flex items-center justify-center text-[#5C6A7D] border border-[#E2E8F0]"><Package size={16} /></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[#0A2540] text-sm truncate">{m.name}</div>
                              <div className="text-xs text-[#5C6A7D] truncate">{m.project_name} · {m.project_manager_name}</div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border ${STATUS_BADGE[m.status] || ""}`}>
                                {m.status?.replace("_", " ")}
                              </span>
                              <div className="text-[10px] text-[#5C6A7D] font-mono">Disponible {m.quantity}</div>
                              {m.has_active_reuse_thread && (
                                <span className="inline-flex items-center px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] font-bold rounded-sm bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" data-testid={`match-in-conversation-${m.id}`}>
                                  En conversación
                                </span>
                              )}
                              {isPicked && (
                                <div className="flex items-center gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
                                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Cant.</span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={rowMax}
                                    value={picked[m.id]}
                                    onChange={(e) => setPickedQty(m, e.target.value)}
                                    className="w-16 h-7 text-center border border-[#E2E8F0] rounded-sm text-xs font-mono"
                                    data-testid={`match-qty-${m.id}`}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky bottom action — oculto para administrador de activos */}
      {!isAssetAdmin && results && results.some((r) => r.matches.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-md border-t border-[#E2E8F0] px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-[#5C6A7D]">
            {pickedList.length === 0
              ? "Selecciona uno o varios items para solicitar su reutilización."
              : `${pickedList.length} item${pickedList.length === 1 ? "" : "s"} · ${pickedList.reduce((s, x) => s + x.quantity, 0)} unidad${pickedList.reduce((s, x) => s + x.quantity, 0) === 1 ? "" : "es"}`}
          </div>
          <Button
            onClick={openReuse}
            disabled={pickedList.length === 0}
            className="rounded-sm bg-[#10B981] hover:bg-[#059669] text-white h-11 px-6 disabled:opacity-40 disabled:pointer-events-none"
            data-testid="bulk-reuse-button"
          >
            <Recycle size={16} className="mr-2" /> Reutilizar
          </Button>
        </div>
      )}
      {isAssetAdmin && results && results.some((r) => r.matches.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#FAFAFA] border-t border-[#E2E8F0] px-4 sm:px-6 lg:px-8 py-3 text-xs text-[#5C6A7D]" data-testid="asset-admin-no-reuse-notice">
          Como administrador de activos, no puedes solicitar reutilizaciones (conflicto de interés con la fijación de precios).
        </div>
      )}

      {/* Reuse message modal */}
      <Dialog open={reuseOpen} onOpenChange={(o) => !o && setReuseOpen(false)}>
        <DialogContent className="rounded-sm">
          {reuseStep === 1 ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">¿Hasta cuándo lo vas a requerir?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="max-h-32 overflow-auto border border-[#E2E8F0] rounded-sm bg-[#FAFAFA] p-2 text-xs" data-testid="reuse-selection-preview-step1">
                  {pickedList.map((it) => (
                    <div key={it.asset.id} className="flex items-center justify-between px-1 py-0.5 text-[#0A2540]">
                      <span className="truncate">{it.asset.name} · {it.asset.project_name}</span>
                      <span className="font-mono text-[#5C6A7D]">×{it.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Fecha de necesidad</Label>
                  <Input
                    type="date"
                    value={neededUntil}
                    onChange={(e) => setNeededUntil(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="rounded-sm h-11"
                    data-testid="bulk-reuse-needed-until"
                  />
                  <p className="text-xs text-[#5C6A7D]">
                    Al completarse la reutilización, este equipamiento estará asignado a tu proyecto hasta la fecha que indiques.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReuseOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button
                  onClick={goToMessageStep}
                  disabled={!neededUntil}
                  className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm"
                  data-testid="bulk-reuse-next-step"
                >
                  Continuar
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Mensaje para el equipo</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-[#5C6A7D]">
                  Se enviará una notificación al equipo de cada proyecto donde se encuentran los items seleccionados.
                </div>
                <div className="max-h-32 overflow-auto border border-[#E2E8F0] rounded-sm bg-[#FAFAFA] p-2 text-xs" data-testid="reuse-selection-preview">
                  {pickedList.map((it) => (
                    <div key={it.asset.id} className="flex items-center justify-between px-1 py-0.5 text-[#0A2540]">
                      <span className="truncate">{it.asset.name} · {it.asset.project_name}</span>
                      <span className="font-mono text-[#5C6A7D]">×{it.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Mensaje</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hola, necesitamos estos artículos para el arranque de la obra. ¿Cuándo podríamos pasar por ellos?"
                    className="rounded-sm min-h-[120px]"
                    data-testid="bulk-reuse-message-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReuseStep(1)} className="rounded-sm">Atrás</Button>
                <Button
                  onClick={sendReuse}
                  disabled={sending}
                  className="bg-[#10B981] hover:bg-[#059669] text-white rounded-sm"
                  data-testid="bulk-reuse-send"
                >
                  {sending ? "Enviando…" : "Enviar solicitud"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
